/* 
* Copyright (c) 2026 Send2Me, media capture and delivery platform. All rights reserved. 
* Distributed under the TruVideo proprietary license. 
*/
import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  RefreshCw,
  PlusCircle,
  ClipboardList,
  FolderOpen,
  Users,
  Building2,
  Layers,
  CheckCircle2,
  ArrowRight,
  ArrowUpRight,
  Eye,
  Video,
  Radio,
  Activity,
  Glasses,
  PhoneOff,
} from 'lucide-react';
import { serviceRequestService } from '../services/serviceRequestService';
import { userService } from '../services/userService';
import { accountService } from '../services/accountService';
import { subAccountService } from '../services/subAccountService';
import { mediaLibraryService } from '../services/mediaLibraryService';
import StatusBadge from '../components/StatusBadge';
import LiveSessionServerNotice, { getLiveSessionIndicator, isLiveSessionServerOffline } from '../components/LiveSessionServerNotice';
import { usePermissions } from '../context/PermissionContext';
import { useAuth } from '../context/AuthContext';
import { useLiveSessionContext } from '../context/LiveSessionContext';
import toastService from '../utils/toastService';
import useServiceRequestTypes from '../hooks/useServiceRequestTypes';
import { resolveTypeName } from '../utils/serviceRequestLabels';
import { getScopeLevel } from '../utils/scope';
import { ACCOUNT_LABEL, SUB_ACCOUNT_LABEL, SERVICE_REQUEST_LABEL } from '../constants/entityLabels';

const PLATFORM_LABELS = {
  xreal: 'XREAL',
  meta: 'Meta',
  snap: 'Snap',
  optaric: 'Optaric',
  samsung: 'Samsung',
};

const isJoinable = (session) => session.status !== 'in_live_session';
const getPlatformLabel = (session) => PLATFORM_LABELS[session.app_platform] || (session.app_platform ? session.app_platform.toUpperCase() : 'Smart Glasses');
const getDisplayName = (session) => session.creator_name || session.device_name || `Room ${session.room}`;

const LiveSessionsEmptyFallback = () => (
  <div className="flex-1 flex flex-col items-center justify-center text-center py-6 sm:py-8 px-4">
    {/* Animated Radar / Broadcast Beacon */}
    <div className="relative flex items-center justify-center mb-3.5">
      <div className="absolute w-20 h-20 rounded-full bg-primary/5 animate-ping opacity-75 pointer-events-none" />
      <div className="absolute w-14 h-14 rounded-full bg-primary/10 animate-pulse pointer-events-none" />
      <div className="relative w-12 h-12 rounded-2xl bg-primary/10 border border-primary/20 flex items-center justify-center text-primary shadow-inner z-10">
        <Radio size={22} className="text-primary animate-pulse" />
      </div>
    </div>

    {/* Live Status Pill */}
    <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-base-200/80 border border-base-content/10 text-[10px] sm:text-[11px] font-bold text-base-content/70 shadow-sm mb-2.5">
      <span className="relative flex h-2 w-2">
        <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-emerald-500 opacity-75" />
        <span className="relative inline-flex rounded-full h-2 w-2 bg-emerald-500" />
      </span>
      <span className="uppercase tracking-wider">Waiting for Live Session</span>
    </div>

    {/* Heading & Subtitle */}
    <h3 className="text-sm sm:text-base font-black text-base-content tracking-tight mb-1">
      No Active Live Sessions
    </h3>
    <p className="text-xs text-base-content/60 font-medium max-w-sm mx-auto leading-relaxed mb-4">
      There are currently no active video streams being broadcast. When a technician or field device starts streaming, it will automatically appear here in real time.
    </p>

    {/* 3-Step Information Guidance Cards */}
    <div className="grid grid-cols-1 sm:grid-cols-3 gap-2.5 w-full max-w-xl text-left">
      <div className="p-3 rounded-2xl bg-base-200/50 border border-base-content/5 flex flex-col gap-1 hover:bg-base-200/80 transition-colors">
        <div className="w-6 h-6 rounded-lg bg-primary/10 flex items-center justify-center text-primary shrink-0">
          <Glasses size={13} />
        </div>
        <div>
          <h4 className="text-[11px] font-black text-base-content uppercase tracking-wider">1. Broadcast</h4>
          <p className="text-[10px] text-base-content/60 font-medium leading-normal mt-0.5">
            Technicians launch session on smart glasses.
          </p>
        </div>
      </div>

      <div className="p-3 rounded-2xl bg-base-200/50 border border-base-content/5 flex flex-col gap-1 hover:bg-base-200/80 transition-colors">
        <div className="w-6 h-6 rounded-lg bg-primary/10 flex items-center justify-center text-primary shrink-0">
          <Activity size={13} />
        </div>
        <div>
          <h4 className="text-[11px] font-black text-base-content uppercase tracking-wider">2. Detected</h4>
          <p className="text-[10px] text-base-content/60 font-medium leading-normal mt-0.5">
            Active streams appear instantly with room details.
          </p>
        </div>
      </div>

      <div className="p-3 rounded-2xl bg-base-200/50 border border-base-content/5 flex flex-col gap-1 hover:bg-base-200/80 transition-colors">
        <div className="w-6 h-6 rounded-lg bg-primary/10 flex items-center justify-center text-primary shrink-0">
          <Video size={13} />
        </div>
        <div>
          <h4 className="text-[11px] font-black text-base-content uppercase tracking-wider">3. Join</h4>
          <p className="text-[10px] text-base-content/60 font-medium leading-normal mt-0.5">
            Connect directly with 2-way audio & video.
          </p>
        </div>
      </div>
    </div>
  </div>
);

const LiveSessionCard = ({ session, joinedRoom, onJoin, onLeave }) => {
  const joinable = isJoinable(session);
  const isJoined = joinedRoom === session.room;
  const disableJoin = !isJoined && (joinedRoom || !joinable);
  const blocked = !isJoined && !joinable;

  return (
    <div
      key={session.room}
      className={`flex items-center px-4 py-4 rounded-2xl border border-base-300 transition-all gap-4 ${blocked ? 'cursor-not-allowed' : 'hover:bg-base-200'}`}
    >
      <div className="flex flex-col min-w-0 flex-shrink-0 w-36 sm:w-40">
        {session.app_name ? (
          <span className="badge badge-ghost badge-sm font-bold truncate w-fit">
            {session.app_name}
          </span>
        ) : <span />}
        <span className="badge badge-outline badge-sm font-bold w-fit mt-1">
          {getPlatformLabel(session)}
        </span>
      </div>

      <div className="flex items-center gap-2 min-w-0 flex-1">
        <span className={`inline-block w-2 h-2 rounded-full flex-shrink-0 ${joinable ? 'bg-success' : 'bg-warning'}`} />
        <div className="flex flex-col min-w-0">
          <span className="text-sm font-black uppercase tracking-tight text-base-content truncate">
            {getDisplayName(session)}
          </span>
          <span className="text-[11px] font-medium text-base-content/50 uppercase tracking-wide truncate">
            Room {session.room}
          </span>
        </div>
      </div>

      <div className="hidden sm:block flex-shrink-0 w-32 md:w-40 text-[11px] font-medium text-base-content/50 truncate">
        {!joinable && session.viewer_name ? `Viewer: ${session.viewer_name}` : ''}
      </div>

      <span className={`badge badge-sm font-bold flex-shrink-0 ${joinable ? 'badge-success text-success-content' : 'badge-warning text-warning-content'}`}>
        {joinable ? 'AVAILABLE' : 'IN CALL'}
      </span>

      <div className="flex-shrink-0">
        {isJoined ? (
          <button
            type="button"
            onClick={onLeave}
            className="btn btn-sm btn-error btn-outline flex-shrink-0 gap-1"
            title="Leave session"
          >
            <PhoneOff size={14} />
            Leave
          </button>
        ) : (
          <button
            type="button"
            onClick={() => onJoin(session)}
            disabled={disableJoin}
            className={`btn btn-sm btn-primary flex-shrink-0 disabled:opacity-30 ${blocked ? 'cursor-not-allowed' : ''}`}
            title={
              disableJoin
                ? (!joinable ? 'Session already in progress' : 'Leave your current session first')
                : 'Join session'
            }
          >
            Join
          </button>
        )}
      </div>
    </div>
  );
};

const formatDate = (date) => {
  if (!date) return '—';
  return new Date(date).toLocaleDateString('en-US', {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
  });
};

const formatDateTime = (date) => {
  if (!date) return '—';
  return new Date(date).toLocaleString('en-US', {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
    hour12: true,
  });
};

const formatRelativeTime = (date) => {
  if (!date) return '';
  const now = new Date();
  const d = new Date(date);
  const diffMs = now.getTime() - d.getTime();

  if (diffMs < 0) return '';

  const diffSec = Math.floor(diffMs / 1000);
  const diffMin = Math.floor(diffSec / 60);
  const diffHour = Math.floor(diffMin / 60);

  if (diffMin < 1) {
    return '';
  }

  if (diffMin < 60) {
    return `${diffMin} ${diffMin === 1 ? 'min' : 'mins'} ago`;
  }

  const isToday = now.toDateString() === d.toDateString();
  if (isToday) {
    return `${diffHour} ${diffHour === 1 ? 'hour' : 'hours'} ago`;
  }

  const yesterday = new Date(now);
  yesterday.setDate(now.getDate() - 1);
  const isYesterday = yesterday.toDateString() === d.toDateString();

  const timeString = d.toLocaleTimeString('en-US', {
    hour: 'numeric',
    minute: '2-digit',
    hour12: true,
  });

  if (isYesterday) {
    return `Yesterday at ${timeString}`;
  }

  const isSameYear = now.getFullYear() === d.getFullYear();
  if (isSameYear) {
    const dateMonth = d.toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
    });
    return `${dateMonth} at ${timeString}`;
  }

  const dateYear = d.toLocaleDateString('en-US', {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
  });
  return `${dateYear} at ${timeString}`;
};

const Dashboard = () => {
  const navigate = useNavigate();
  const { can } = usePermissions();
  const { user } = useAuth();
  const scopeLevel = getScopeLevel(user);

  const {
    sessions: liveSessions = [],
    joinedRoom: liveSessionJoinedRoom,
    join: joinLiveSession,
    leave: leaveLiveSession,
    authorized: liveSessionAuthorized,
    ready: liveSessionReady,
    serverStatus: liveSessionServerStatus,
    reconnect: reconnectLiveSession,
    reconnectAttemptsLeft: liveSessionReconnectAttemptsLeft,
  } = useLiveSessionContext();
  const showLiveSessionsCard = can.service_requests.view() && liveSessionAuthorized;
  const liveSessionOffline = isLiveSessionServerOffline(liveSessionServerStatus);
  const liveSessionReconnecting = liveSessionServerStatus === 'reconnecting';
  // Keep a still-listed session visible during a brief reconnect; otherwise explain the outage.
  const showLiveSessionNotice = liveSessionServerStatus === 'disconnected'
    || (liveSessionReconnecting && liveSessions.length === 0);
  const liveSessionIndicator = getLiveSessionIndicator(liveSessionServerStatus);
  const LiveSessionIcon = liveSessionIndicator.Icon;

  const handleJoinLiveSession = async (session) => {
    try {
      await joinLiveSession(session);
    } catch {
      toastService.error('Failed to join the live session');
    }
  };

  const [counts, setCounts] = useState({ all: 0, open: 0, closed: 0 });
  const [scopedCounts, setScopedCounts] = useState({ users: 0, accounts: 0, subAccounts: 0, media: 0 });
  const [fetchFailures, setFetchFailures] = useState({ counts: false, recent: false, users: false, accounts: false, subAccounts: false, media: false });
  const [recentRequests, setRecentRequests] = useState([]);
  const [loading, setLoading] = useState(true);
  const [isRefreshing, setIsRefreshing] = useState(false);
  const [error, setError] = useState('');
  const [lastRefreshedAt, setLastRefreshedAt] = useState(() => new Date());
  const [relativeRefreshTime, setRelativeRefreshTime] = useState(() => formatRelativeTime(new Date()));

  const showAccountColumn = !user?.accountUID;
  const showSubAccountColumn = !user?.subAccountUID;

  const { types } = useServiceRequestTypes(user?.subAccountUID || undefined, {
    requireSubAccountUid: false,
    includeInactive: true,
  });

  const fetchDashboardData = useCallback(async (isManualRefresh = false) => {
    const canServiceRequests = can.service_requests.view();
    const canUsers = can.users.view();
    const canAccounts = can.accounts.view() && scopeLevel === 'platform';
    const canSubAccounts = can.sub_accounts.view() && scopeLevel !== 'sub_account';

    if (!canServiceRequests && !canUsers && !canAccounts && !canSubAccounts) {
      setLoading(false);
      return;
    }

    if (isManualRefresh) {
      setIsRefreshing(true);
    } else {
      setLoading(true);
    }
    setError('');

    try {
      const [srRes, recentSrRes, usersRes, accountsRes, subAccountsRes, mediaRes] = await Promise.allSettled([
        canServiceRequests ? serviceRequestService.getStatusCounts() : Promise.resolve(null),
        canServiceRequests ? serviceRequestService.getServiceRequests({ page: 1, limit: 7, sortBy: 'createdAt', sortDir: 'DESC' }) : Promise.resolve(null),
        canUsers ? userService.getUsers({ limit: 1 }) : Promise.resolve(null),
        canAccounts ? accountService.getAccounts({ limit: 1 }) : Promise.resolve(null),
        canSubAccounts ? subAccountService.getSubAccounts(user?.accountUID || null, { limit: 1 }) : Promise.resolve(null),
        canServiceRequests ? mediaLibraryService.getMediaItems({ page: 1, limit: 1 }) : Promise.resolve(null),
      ]);

      const attempted = [
        canServiceRequests && srRes,
        canServiceRequests && recentSrRes,
        canUsers && usersRes,
        canAccounts && accountsRes,
        canSubAccounts && subAccountsRes,
        canServiceRequests && mediaRes,
      ].filter(Boolean);

      const allFailed = attempted.length > 0 && attempted.every((res) => res.status === 'rejected');

      if (srRes.status === 'fulfilled' && srRes.value) {
        setCounts(srRes.value.data || { all: 0, open: 0, closed: 0 });
      }

      if (recentSrRes.status === 'fulfilled' && recentSrRes.value) {
        setRecentRequests(recentSrRes.value?.data?.data || []);
      }

      setScopedCounts({
        users: usersRes.status === 'fulfilled' ? (usersRes.value?.data?.total ?? 0) : 0,
        accounts: accountsRes.status === 'fulfilled' ? (accountsRes.value?.data?.pagination?.total ?? 0) : 0,
        subAccounts: subAccountsRes.status === 'fulfilled' ? (subAccountsRes.value?.data?.pagination?.total ?? 0) : 0,
        media: mediaRes.status === 'fulfilled' ? (mediaRes.value?.data?.total ?? 0) : 0,
      });

      setFetchFailures({
        counts: canServiceRequests && srRes.status === 'rejected',
        recent: canServiceRequests && recentSrRes.status === 'rejected',
        users: canUsers && usersRes.status === 'rejected',
        accounts: canAccounts && accountsRes.status === 'rejected',
        subAccounts: canSubAccounts && subAccountsRes.status === 'rejected',
        media: canServiceRequests && mediaRes.status === 'rejected',
      });

      if (allFailed) {
        setError('Failed to load dashboard data');
      } else {
        setLastRefreshedAt(new Date());
      }
    } catch {
      setError('An unexpected error occurred while fetching dashboard metrics');
    } finally {
      setLoading(false);
      setIsRefreshing(false);
    }
  }, [can, scopeLevel, user?.accountUID]);

  useEffect(() => {
    fetchDashboardData();
    // Re-fetch when the switched sub-account changes (API scope follows the active sub-account).
  }, [fetchDashboardData, user?.subAccountUID]);

  useEffect(() => {
    if (!lastRefreshedAt) return;
    setRelativeRefreshTime(formatRelativeTime(lastRefreshedAt));

    const interval = setInterval(() => {
      setRelativeRefreshTime(formatRelativeTime(lastRefreshedAt));
    }, 30000);

    return () => clearInterval(interval);
  }, [lastRefreshedAt]);

  const handleStateClick = (status) => {
    navigate('/service-requests', { state: { status } });
  };

  const totalRequests = counts.all || 0;
  const openPercentage = totalRequests > 0 ? Math.round(((counts.open || 0) / totalRequests) * 100) : 0;
  const closedPercentage = totalRequests > 0 ? Math.round(((counts.closed || 0) / totalRequests) * 100) : 0;

  const currentScopeTitle = useMemo(() => {
    if (scopeLevel === 'platform') return 'Platform Overview';
    if (scopeLevel === 'account') return user?.accountName || `${ACCOUNT_LABEL.singular} Workspace`;
    return user?.subAccountName || `${SUB_ACCOUNT_LABEL.singular} Workspace`;
  }, [scopeLevel, user?.accountName, user?.subAccountName]);

  const currentScopeBadge = useMemo(() => {
    if (scopeLevel === 'platform') return { label: 'Platform Scope', class: 'badge-primary' };
    if (scopeLevel === 'account') return { label: `${ACCOUNT_LABEL.singular} Scope`, class: 'badge-accent' };
    return { label: `${SUB_ACCOUNT_LABEL.singular} Scope`, class: 'badge-info' };
  }, [scopeLevel]);

  const visibleCardsCount = useMemo(() => {
    let count = 0;
    if (can.service_requests.view()) count += 3; // Total Requests, Resolution, Media Items
    if (can.users.view()) count += 1; // Users
    if (can.accounts.view() && scopeLevel === 'platform') count += 1; // Accounts
    if (can.sub_accounts.view() && scopeLevel !== 'sub_account') count += 1; // Sub-Accounts
    return count;
  }, [can, scopeLevel]);

  const kpiGridClass = useMemo(() => {
    switch (visibleCardsCount) {
      case 1:
        return 'grid-cols-1';
      case 2:
        return 'grid-cols-1 sm:grid-cols-2';
      case 3:
        return 'grid-cols-1 sm:grid-cols-3';
      case 4:
        return 'grid-cols-1 sm:grid-cols-2 lg:grid-cols-4';
      case 5:
        return 'grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-5';
      case 6:
        return 'grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-6';
      default:
        return visibleCardsCount > 6
          ? 'grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-6'
          : 'grid-cols-1 sm:grid-cols-2 lg:grid-cols-4';
    }
  }, [visibleCardsCount]);

  const skeletonCount = useMemo(() => {
    return visibleCardsCount || 4;
  }, [visibleCardsCount]);

  return (
    <div className="w-full space-y-4 pb-2">
      {/* Hero Header Card matching ServiceRequests layout */}
      <div className="glass-card rounded-2xl p-4 sm:p-5 shadow-sm border border-base-content/10">
        <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
          <div className="space-y-1">
            <div className="flex items-center gap-2 flex-wrap">
              <h1 className="text-2xl sm:text-3xl font-black text-base-content tracking-tight">
                Dashboard
              </h1>
              <span className={`badge ${currentScopeBadge.class} badge-sm font-bold tracking-wider uppercase text-[10px] py-2 px-2.5`}>
                {currentScopeBadge.label}
              </span>
            </div>
            <p className="text-xs text-base-content/50">
              Overview of media capture operations, {SERVICE_REQUEST_LABEL.pluralLower}, and activity for <strong className="text-base-content/80 font-bold">{currentScopeTitle}</strong>.
            </p>
          </div>

          <div className="flex items-center gap-2.5 flex-wrap">
            <div
              className="tooltip tooltip-bottom tooltip-primary before:text-xs before:font-semibold before:whitespace-nowrap"
              data-tip={
                lastRefreshedAt
                  ? `Last refreshed: ${formatDateTime(lastRefreshedAt)}`
                  : 'Refresh Dashboard Data'
              }
            >
              <button
                type="button"
                onClick={() => fetchDashboardData(true)}
                disabled={loading || isRefreshing}
                className="btn btn-sm btn-ghost border border-base-content/10 rounded-xl gap-2 font-bold shadow-sm hover:bg-base-content/5 transition-all text-xs"
                aria-label="Refresh Dashboard Data"
              >
                <RefreshCw size={14} className={isRefreshing ? 'animate-spin text-primary' : ''} />
                <span>Refresh</span>
                {relativeRefreshTime && (
                  <span className="text-[11px] font-medium text-base-content/60 border-l border-base-content/20 pl-2 ml-0.5 whitespace-nowrap">
                    {relativeRefreshTime}
                  </span>
                )}
              </button>
            </div>

            {can.service_requests.view() && (
              <button
                onClick={() => navigate('/media-library')}
                className="btn btn-sm btn-ghost border border-base-content/10 rounded-xl gap-2 font-bold shadow-sm hover:bg-base-content/5 transition-all text-xs"
              >
                <FolderOpen size={14} className="text-info" />
                <span>Media Library</span>
              </button>
            )}

            {can.service_requests.create() && (
              <button
                onClick={() => navigate('/service-requests/new')}
                className="btn btn-sm btn-primary rounded-xl gap-2 font-bold shadow-sm hover:shadow transition-all text-xs"
              >
                <PlusCircle size={15} />
                <span>New {SERVICE_REQUEST_LABEL.singular}</span>
              </button>
            )}
          </div>
        </div>
      </div>

      {error && (
        <div className="bg-error/10 border border-error/20 text-error px-4 py-3 rounded-2xl text-xs sm:text-sm font-bold flex items-center justify-between gap-3">
          <span>{error}</span>
          <button
            onClick={() => fetchDashboardData(true)}
            className="btn btn-xs btn-outline btn-error rounded-lg"
          >
            Retry
          </button>
        </div>
      )}

      {/* Main KPI Stat Cards Grid (Single Row on Desktop dynamically filling full width for 3, 4, 5, 6 cards) */}
      {visibleCardsCount > 0 && (
        loading ? (
          <div className={`grid ${kpiGridClass} gap-3.5 sm:gap-4`}>
            {Array.from({ length: skeletonCount }).map((_, i) => (
              <div key={i} className="glass-card rounded-2xl p-4 sm:p-5 border border-base-content/10 space-y-3 animate-pulse min-w-0">
                <div className="flex justify-between items-center">
                  <div className="h-3 w-20 bg-base-300 rounded"></div>
                  <div className="h-8 w-8 bg-base-300 rounded-xl"></div>
                </div>
                <div className="h-8 w-24 bg-base-300 rounded-lg"></div>
                <div className="h-2.5 w-32 bg-base-200 rounded"></div>
              </div>
            ))}
          </div>
        ) : (
          <div className={`grid ${kpiGridClass} gap-3.5 sm:gap-4`}>
            {can.service_requests.view() && (
              <>
                {/* Total Service Requests */}
                <button
                  type="button"
                  onClick={() => handleStateClick('All')}
                  className="glass-card rounded-2xl p-4 sm:p-5 border border-base-content/10 hover:border-primary/50 hover:shadow-md transition-all cursor-pointer group relative overflow-hidden flex flex-col justify-between min-w-0 w-full text-left"
                >
                  <div className="absolute top-0 left-0 right-0 h-1 bg-primary"></div>
                  <div>
                    <div className="flex items-center justify-between mb-2 gap-1">
                      <span className="text-[10px] font-black uppercase tracking-widest text-base-content/50 truncate">
                        Total {SERVICE_REQUEST_LABEL.plural}
                      </span>
                      <div className="w-8 h-8 rounded-xl bg-primary/10 text-primary flex items-center justify-center group-hover:scale-110 transition-transform flex-shrink-0">
                        <ClipboardList size={16} />
                      </div>
                    </div>
                    <div className="text-2xl xl:text-3xl font-black text-base-content group-hover:text-primary transition-colors tracking-tight">
                      {fetchFailures.counts ? (
                        <span className="text-warning" title="Failed to load — click Refresh to retry">—</span>
                      ) : totalRequests}
                    </div>
                  </div>
                  <div className="mt-2 text-[11px] xl:text-xs text-base-content/50 flex items-center justify-between pt-1 gap-1">
                    {fetchFailures.counts ? (
                      <span className="text-warning truncate" title="Failed to load — click Refresh to retry">— open · — closed</span>
                    ) : (
                      <span className="truncate">{counts.open || 0} open · {counts.closed || 0} closed</span>
                    )}
                    <ArrowUpRight size={13} className="text-base-content/30 group-hover:text-primary group-hover:translate-x-0.5 group-hover:-translate-y-0.5 transition-all flex-shrink-0 ml-1" />
                  </div>
                </button>

                {/* Resolution Status */}
                <div className="glass-card rounded-2xl p-4 sm:p-5 border border-base-content/10 relative overflow-hidden flex flex-col justify-between min-w-0">
                  <div className="absolute top-0 left-0 right-0 h-1 bg-primary"></div>
                  <div>
                    <div className="flex items-center justify-between mb-2 gap-1">
                      <span className="text-[10px] font-black uppercase tracking-widest text-base-content/50 truncate">
                        Resolution
                      </span>
                      <div className="w-8 h-8 rounded-xl bg-primary/10 text-primary flex items-center justify-center flex-shrink-0">
                        <CheckCircle2 size={16} />
                      </div>
                    </div>

                    {fetchFailures.counts ? (
                      <div className="w-full h-2 bg-base-200 rounded-full overflow-hidden flex my-2" title="Failed to load — click Refresh to retry">
                        <div className="w-full h-full bg-warning/30"></div>
                      </div>
                    ) : (
                      <div className="w-full h-2 bg-base-200 rounded-full overflow-hidden flex my-2">
                        <div
                          className="bg-warning transition-all duration-500"
                          style={{ width: `${openPercentage}%` }}
                          title={`Open: ${counts.open || 0} (${openPercentage}%)`}
                        ></div>
                        <div
                          className="bg-success transition-all duration-500"
                          style={{ width: `${closedPercentage}%` }}
                          title={`Closed: ${counts.closed || 0} (${closedPercentage}%)`}
                        ></div>
                      </div>
                    )}
                  </div>
                  <div className="flex items-center justify-between text-[10px] xl:text-[11px] font-semibold text-base-content/60 pt-1 gap-1">
                    <button
                      type="button"
                      onClick={() => handleStateClick('open')}
                      className="flex items-center gap-1 hover:underline truncate"
                    >
                      <span className="w-2 h-2 rounded-full bg-warning flex-shrink-0"></span>
                      <span className="truncate">
                        {fetchFailures.counts ? (
                          <span className="text-warning" title="Failed to load — click Refresh to retry">Open (—)</span>
                        ) : `Open (${openPercentage}%)`}
                      </span>
                    </button>
                    <button
                      type="button"
                      onClick={() => handleStateClick('closed')}
                      className="flex items-center gap-1 hover:underline truncate"
                    >
                      <span className="w-2 h-2 rounded-full bg-success flex-shrink-0"></span>
                      <span className="truncate">
                        {fetchFailures.counts ? (
                          <span className="text-warning" title="Failed to load — click Refresh to retry">Closed (—)</span>
                        ) : `Closed (${closedPercentage}%)`}
                      </span>
                    </button>
                  </div>
                </div>

                {/* Media Items */}
                <button
                  type="button"
                  onClick={() => navigate('/media-library')}
                  className="glass-card rounded-2xl p-4 sm:p-5 border border-base-content/10 hover:border-primary/50 hover:shadow-md transition-all cursor-pointer group relative overflow-hidden flex flex-col justify-between min-w-0 w-full text-left"
                >
                  <div className="absolute top-0 left-0 right-0 h-1 bg-primary"></div>
                  <div>
                    <div className="flex items-center justify-between mb-2 gap-1">
                      <span className="text-[10px] font-black uppercase tracking-widest text-base-content/50 truncate">
                        Media Items
                      </span>
                      <div className="w-8 h-8 rounded-xl bg-primary/10 text-primary flex items-center justify-center group-hover:scale-110 transition-transform flex-shrink-0">
                        <FolderOpen size={16} />
                      </div>
                    </div>
                    <div className="text-2xl xl:text-3xl font-black text-base-content group-hover:text-primary transition-colors tracking-tight">
                      {fetchFailures.media ? (
                        <span className="text-warning" title="Failed to load — click Refresh to retry">—</span>
                      ) : scopedCounts.media}
                    </div>
                  </div>
                  <div className="mt-2 text-[11px] xl:text-xs text-base-content/50 flex items-center justify-between pt-1 gap-1">
                    <span className="truncate">Captured media</span>
                    <ArrowUpRight size={13} className="text-base-content/30 group-hover:text-primary group-hover:translate-x-0.5 group-hover:-translate-y-0.5 transition-all flex-shrink-0 ml-1" />
                  </div>
                </button>
              </>
            )}

            {/* Accounts (Platform Scope only) */}
            {can.accounts.view() && scopeLevel === 'platform' && (
              <button
                type="button"
                onClick={() => navigate('/accounts')}
                className="glass-card rounded-2xl p-4 sm:p-5 border border-base-content/10 hover:border-primary/50 hover:shadow-md transition-all cursor-pointer group relative overflow-hidden flex flex-col justify-between min-w-0 w-full text-left"
              >
                <div className="absolute top-0 left-0 right-0 h-1 bg-primary"></div>
                <div>
                  <div className="flex items-center justify-between mb-2 gap-1">
                    <span className="text-[10px] font-black uppercase tracking-widest text-base-content/50 truncate">
                      {ACCOUNT_LABEL.plural}
                    </span>
                    <div className="w-8 h-8 rounded-xl bg-primary/10 text-primary flex items-center justify-center group-hover:scale-110 transition-transform flex-shrink-0">
                      <Building2 size={16} />
                    </div>
                  </div>
                  <div className="text-2xl xl:text-3xl font-black text-base-content group-hover:text-primary transition-colors tracking-tight">
                    {fetchFailures.accounts ? (
                      <span className="text-warning" title="Failed to load — click Refresh to retry">—</span>
                    ) : scopedCounts.accounts}
                  </div>
                </div>
                <div className="mt-2 text-[11px] xl:text-xs text-base-content/50 flex items-center justify-between pt-1 gap-1">
                  <span className="truncate">Organizations</span>
                  <ArrowUpRight size={13} className="text-base-content/30 group-hover:text-primary group-hover:translate-x-0.5 group-hover:-translate-y-0.5 transition-all flex-shrink-0 ml-1" />
                </div>
              </button>
            )}

            {/* Sub-Accounts (Platform and Account scope) */}
            {can.sub_accounts.view() && scopeLevel !== 'sub_account' && (
              <button
                type="button"
                onClick={() => navigate('/sub-accounts')}
                className="glass-card rounded-2xl p-4 sm:p-5 border border-base-content/10 hover:border-primary/50 hover:shadow-md transition-all cursor-pointer group relative overflow-hidden flex flex-col justify-between min-w-0 w-full text-left"
              >
                <div className="absolute top-0 left-0 right-0 h-1 bg-primary"></div>
                <div>
                  <div className="flex items-center justify-between mb-2 gap-1">
                    <span className="text-[10px] font-black uppercase tracking-widest text-base-content/50 truncate">
                      {SUB_ACCOUNT_LABEL.plural}
                    </span>
                    <div className="w-8 h-8 rounded-xl bg-primary/10 text-primary flex items-center justify-center group-hover:scale-110 transition-transform flex-shrink-0">
                      <Layers size={16} />
                    </div>
                  </div>
                  <div className="text-2xl xl:text-3xl font-black text-base-content group-hover:text-primary transition-colors tracking-tight">
                    {fetchFailures.subAccounts ? (
                      <span className="text-warning" title="Failed to load — click Refresh to retry">—</span>
                    ) : scopedCounts.subAccounts}
                  </div>
                </div>
                <div className="mt-2 text-[11px] xl:text-xs text-base-content/50 flex items-center justify-between pt-1 gap-1">
                  <span className="truncate">Locations</span>
                  <ArrowUpRight size={13} className="text-base-content/30 group-hover:text-primary group-hover:translate-x-0.5 group-hover:-translate-y-0.5 transition-all flex-shrink-0 ml-1" />
                </div>
              </button>
            )}

            {/* Users */}
            {can.users.view() && (
              <button
                type="button"
                onClick={() => navigate('/users')}
                className="glass-card rounded-2xl p-4 sm:p-5 border border-base-content/10 hover:border-primary/50 hover:shadow-md transition-all cursor-pointer group relative overflow-hidden flex flex-col justify-between min-w-0 w-full text-left"
              >
                <div className="absolute top-0 left-0 right-0 h-1 bg-primary"></div>
                <div>
                  <div className="flex items-center justify-between mb-2 gap-1">
                    <span className="text-[10px] font-black uppercase tracking-widest text-base-content/50 truncate">
                      Users
                    </span>
                    <div className="w-8 h-8 rounded-xl bg-primary/10 text-primary flex items-center justify-center group-hover:scale-110 transition-transform flex-shrink-0">
                      <Users size={16} />
                    </div>
                  </div>
                  <div className="text-2xl xl:text-3xl font-black text-base-content group-hover:text-primary transition-colors tracking-tight">
                    {fetchFailures.users ? (
                      <span className="text-warning" title="Failed to load — click Refresh to retry">—</span>
                    ) : scopedCounts.users}
                  </div>
                </div>
                <div className="mt-2 text-[11px] xl:text-xs text-base-content/50 flex items-center justify-between pt-1 gap-1">
                  <span className="truncate">Active users</span>
                  <ArrowUpRight size={13} className="text-base-content/30 group-hover:text-primary group-hover:translate-x-0.5 group-hover:-translate-y-0.5 transition-all flex-shrink-0 ml-1" />
                </div>
              </button>
            )}
          </div>
        )
      )}

      {/* Upper Two-Column Grid: Live Sessions (Left) and Recent Service Requests (Right) */}
      {!liveSessionReady ? (
        // Whether the Live Sessions card renders at all isn't known until the live
        // session connection/authorization outcome is determined -- show a neutral
        // skeleton instead of the real grid to avoid a layout flicker/pop-in.
        <div className="grid grid-cols-1 xl:grid-cols-2 gap-4 items-stretch">
          {[0, 1].map((i) => (
            <div key={i} className="glass-card rounded-2xl shadow-sm border border-base-content/10 overflow-hidden p-4 sm:p-5 h-[490px] space-y-3 animate-pulse">
              <div className="flex items-center gap-2.5">
                <div className="w-7 h-7 rounded-lg bg-base-300"></div>
                <div className="h-4 w-32 bg-base-300 rounded"></div>
              </div>
              <div className="space-y-2.5 pt-2">
                {[1, 2, 3, 4, 5].map((row) => (
                  <div key={row} className="h-10 bg-base-200/70 rounded-xl"></div>
                ))}
              </div>
            </div>
          ))}
        </div>
      ) : (
      <div className="grid grid-cols-1 xl:grid-cols-2 gap-4 items-stretch">
        {/* Left Half: Live Sessions */}
        {showLiveSessionsCard && (
          <div className="glass-card rounded-2xl shadow-sm border border-base-content/10 overflow-hidden p-0 relative flex flex-col h-[490px]">
            {/* Header bar */}
            <div className="p-4 sm:p-5 border-b border-base-content/10 flex flex-col sm:flex-row sm:items-center justify-between gap-3 bg-base-100/50 flex-shrink-0">
              <div className="flex items-center gap-2.5">
                {liveSessionOffline ? (
                  <div className={`w-7 h-7 rounded-lg flex items-center justify-center ${liveSessionIndicator.boxClass}`} title={liveSessionIndicator.label}>
                    <LiveSessionIcon size={15} className={liveSessionIndicator.iconClass} />
                  </div>
                ) : (
                <div className={`w-7 h-7 rounded-lg flex items-center justify-center ${liveSessions.length > 0 ? 'bg-emerald-500/10 text-emerald-600 dark:text-emerald-400' : 'bg-primary/10 text-primary'
                  }`}>
                  <Radio size={15} className={liveSessions.length > 0 ? 'animate-pulse' : ''} />
                </div>
                )}
                <div>
                  <div className="flex items-center gap-2">
                    <h2 className="text-sm sm:text-base font-black text-base-content tracking-tight">
                      Live Sessions
                    </h2>
                    {liveSessionServerStatus === 'disconnected' && (
                      <span className="badge badge-sm badge-error badge-outline font-black text-[10px] uppercase tracking-wider py-1.5 px-2">
                        Offline
                      </span>
                    )}
                    {liveSessionReconnecting && (
                      <span className="badge badge-sm badge-warning text-warning-content font-black text-[10px] uppercase tracking-wider py-1.5 px-2">
                        Reconnecting
                      </span>
                    )}
                    {!liveSessionOffline && (liveSessions.length > 0 ? (
                      <span className="badge badge-sm badge-error text-white font-black text-[10px] uppercase tracking-wider py-1.5 px-2 gap-1 animate-pulse">
                        <span className="w-1.5 h-1.5 rounded-full bg-white"></span>
                        <span>{liveSessions.length} Live</span>
                      </span>
                    ) : (
                      <span className="badge badge-sm badge-ghost text-base-content/60 font-bold text-[10px] uppercase tracking-wider py-1.5 px-2">
                        0 Active
                      </span>
                    ))}
                  </div>
                  <p className="text-[11px] text-base-content/40">
                    Real-time video from live streams
                  </p>
                </div>
              </div>
            </div>

            {/* Live Sessions Content */}
            <div className="flex-1 flex flex-col min-h-0 overflow-hidden">
              {showLiveSessionNotice && (
                <LiveSessionServerNotice
                  status={liveSessionServerStatus}
                  compact
                  onReconnect={reconnectLiveSession}
                  reconnectAttemptsLeft={liveSessionReconnectAttemptsLeft}
                />
              )}
              {!showLiveSessionNotice && (liveSessions.length === 0 ? (
                <LiveSessionsEmptyFallback />
              ) : (
                <div className="p-4 sm:p-5 overflow-y-auto flex-1 flex flex-col gap-3">
                  {liveSessions.map((session) => (
                    <LiveSessionCard
                      key={session.room}
                      session={session}
                      joinedRoom={liveSessionJoinedRoom}
                      onJoin={handleJoinLiveSession}
                      onLeave={leaveLiveSession}
                    />
                  ))}
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Right Half: Recent Service Requests */}
        <div className={`glass-card rounded-2xl shadow-sm border border-base-content/10 overflow-hidden p-0 relative flex flex-col h-[490px] ${showLiveSessionsCard ? '' : 'xl:col-span-2'}`}>
          {/* Header bar */}
          <div className="p-4 sm:p-5 border-b border-base-content/10 flex flex-col sm:flex-row sm:items-center justify-between gap-3 bg-base-100/50 flex-shrink-0">
            <div className="flex items-center gap-2.5">
              <div className="w-7 h-7 rounded-lg bg-primary/10 text-primary flex items-center justify-center">
                <ClipboardList size={15} />
              </div>
              <div>
                <h2 className="text-sm sm:text-base font-black text-base-content tracking-tight">
                  Recent {SERVICE_REQUEST_LABEL.plural}
                </h2>
                <p className="text-[11px] text-base-content/40">
                  Latest incoming {SERVICE_REQUEST_LABEL.pluralLower} and job tickets
                </p>
              </div>
            </div>

            {can.service_requests.view() && (
              <button
                onClick={() => navigate('/service-requests')}
                className="btn btn-xs sm:btn-sm btn-ghost text-primary text-xs font-bold gap-1.5 hover:underline pl-0 sm:pl-3 self-start sm:self-center"
              >
                <span>View all</span>
                <ArrowRight size={13} />
              </button>
            )}
          </div>

          {/* Table Content */}
          <div className="flex-1 flex flex-col min-h-0 overflow-hidden">
            {loading ? (
              <div className="p-6 space-y-3 flex-1 flex flex-col justify-center">
                {[1, 2, 3, 4, 5, 6].map((i) => (
                  <div key={i} className="h-10 bg-base-200/50 rounded-xl animate-pulse"></div>
                ))}
              </div>
            ) : !can.service_requests.view() ? (
              <div className="py-14 px-6 flex-1 flex flex-col items-center justify-center text-center space-y-2">
                <div className="w-12 h-12 rounded-2xl bg-base-200 flex items-center justify-center text-base-content/30">
                  <ClipboardList size={22} />
                </div>
                <h3 className="text-sm font-bold text-base-content">Access Restricted</h3>
                <p className="text-xs text-base-content/40 max-w-xs">
                  You do not have permission to view {SERVICE_REQUEST_LABEL.pluralLower} in this workspace.
                </p>
              </div>
            ) : fetchFailures.recent && recentRequests.length === 0 ? (
              <div className="py-14 px-6 flex-1 flex flex-col items-center justify-center text-center space-y-3">
                <div className="w-14 h-14 rounded-2xl bg-warning/10 flex items-center justify-center text-warning shadow-inner">
                  <ClipboardList size={26} />
                </div>
                <div>
                  <h3 className="text-sm sm:text-base font-black text-base-content">
                    Failed to load recent {SERVICE_REQUEST_LABEL.pluralLower}
                  </h3>
                  <p className="text-xs text-base-content/40 max-w-sm mt-0.5">
                    Click Refresh to try again.
                  </p>
                </div>
              </div>
            ) : recentRequests.length === 0 ? (
              <div className="py-14 px-6 flex-1 flex flex-col items-center justify-center text-center space-y-3">
                <div className="w-14 h-14 rounded-2xl bg-base-200 flex items-center justify-center text-base-content/30 shadow-inner">
                  <ClipboardList size={26} />
                </div>
                <div>
                  <h3 className="text-sm sm:text-base font-black text-base-content">
                    No {SERVICE_REQUEST_LABEL.pluralLower} found
                  </h3>
                  <p className="text-xs text-base-content/40 max-w-sm mt-0.5">
                    Start by generating your first {SERVICE_REQUEST_LABEL.singularLower} to track media and inspections.
                  </p>
                </div>
                {can.service_requests.create() && (
                  <button
                    type="button"
                    onClick={() => navigate('/service-requests/new')}
                    className="btn btn-sm btn-primary rounded-xl font-bold gap-2 text-xs"
                  >
                    <PlusCircle size={14} />
                    <span>Create {SERVICE_REQUEST_LABEL.singular}</span>
                  </button>
                )}
              </div>
            ) : (
              <div className="overflow-x-auto flex-1">
                {fetchFailures.recent && (
                  <div className="px-4 py-2 text-[11px] font-semibold text-warning bg-warning/10 border-b border-warning/20">
                    Showing previously loaded data — refresh failed to update recent {SERVICE_REQUEST_LABEL.pluralLower}.
                  </div>
                )}
                <table className="table w-full text-left">
                  <thead>
                    <tr className="bg-base-200/40 border-b border-base-content/5 text-[10px] font-black uppercase tracking-widest text-base-content/40">
                      <th className="py-3 px-4">{SERVICE_REQUEST_LABEL.singular} #</th>
                      <th className="py-3 px-3 whitespace-nowrap">Type</th>
                      <th className="py-3 px-3 whitespace-nowrap">Customer</th>
                      {showAccountColumn && <th className="py-3 px-3 whitespace-nowrap">{ACCOUNT_LABEL.singular}</th>}
                      {showSubAccountColumn && <th className="py-3 px-3 whitespace-nowrap">{SUB_ACCOUNT_LABEL.singular}</th>}
                      <th className="py-3 px-3 text-center whitespace-nowrap">Status</th>
                      <th className="py-3 px-3 whitespace-nowrap">Created</th>
                      <th className="py-3 px-4 text-right whitespace-nowrap">Action</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-base-content/5 text-xs">
                    {recentRequests.map((sr) => (
                      <tr key={sr.uid} className="hover:bg-base-200/40 transition-colors group">
                        {/* Request Number */}
                        <td className="py-3 px-4 font-bold whitespace-nowrap">
                          <button
                            type="button"
                            onClick={() => navigate(`/service-requests/${sr.uid}`)}
                            className="text-primary hover:underline font-bold text-xs tracking-tight text-left"
                          >
                            {sr.requestNumber}
                          </button>
                        </td>

                        {/* Type */}
                        <td className="py-3 px-3 font-medium text-base-content/70 whitespace-nowrap">
                          {resolveTypeName(sr.type, types) || sr.typeLabel || sr.type}
                        </td>

                        {/* Customer */}
                        <td className="py-3 px-3 font-bold text-base-content max-w-[110px] truncate" title={sr.customerName}>
                          {sr.customerName || '—'}
                        </td>

                        {/* Account (platform scope) */}
                        {showAccountColumn && (
                          <td className="py-3 px-3 text-base-content/60 font-medium max-w-[100px] truncate" title={sr.accountName}>
                            {sr.accountName || '—'}
                          </td>
                        )}

                        {/* Sub Account (platform & account scope) */}
                        {showSubAccountColumn && (
                          <td className="py-3 px-3 text-base-content/60 font-medium max-w-[100px] truncate" title={sr.subAccountName}>
                            {sr.subAccountName || '—'}
                          </td>
                        )}

                        {/* Status */}
                        <td className="py-3 px-3 text-center whitespace-nowrap">
                          <StatusBadge status={sr.status} />
                        </td>

                        {/* Created */}
                        <td className="py-3 px-3 text-base-content/70 font-medium text-[11px] whitespace-nowrap">
                          {sr.createdAt ? (
                            <div
                              className="tooltip tooltip-top tooltip-primary before:text-xs before:font-semibold before:whitespace-nowrap"
                              data-tip={formatDateTime(sr.createdAt)}
                            >
                              <span className="cursor-default hover:text-base-content transition-colors">
                                {formatDate(sr.createdAt)}
                              </span>
                            </div>
                          ) : (
                            '—'
                          )}
                        </td>

                        {/* Action */}
                        <td className="py-3 px-4 text-right whitespace-nowrap">
                          <div
                            className="tooltip tooltip-left tooltip-primary before:text-xs before:font-semibold before:whitespace-nowrap"
                            data-tip="View Details"
                          >
                            <button
                              type="button"
                              onClick={() => navigate(`/service-requests/${sr.uid}`)}
                              className="btn btn-xs btn-ghost border border-base-content/10 rounded-lg hover:border-primary hover:text-primary gap-1 font-bold"
                              aria-label="View Details"
                            >
                              <Eye size={12} />
                              <span className="hidden sm:inline">View</span>
                            </button>
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        </div>
      </div>
      )}
    </div>
  );
};

export default Dashboard;
